import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { useHistory, useLocation } from 'react-router-dom';

import * as paths from 'app-router/paths';
import { useStore } from 'stores';
import { Feature } from 'stores/FeatureStore';

import Loading from 'components/Loading';
import Pagination from 'components/Pagination';
import { ErrorMessage, Message } from 'components/Message';
import { FindIcon, LabeledIcon, ReloadIcon } from 'components/Icon';

import { FeatureList } from './TableView';
import { SimpleInlineHelp } from 'components/InlineHelp';

export const Page: React.FC = observer(() =>
{
  const { uiStore, featureStore } = useStore();
  const history = useHistory();
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const pageFromQuery = parseInt(query.get('p') || '1');
  const page = pageFromQuery ? pageFromQuery - 1 : 0;
  const searchQuery = query.get('q') || '';
  const [searchQueryCollector, setSearchQueryCollector] = useState(searchQuery);

  useEffect(() =>
  {
    featureStore.findAsync(searchQuery, page);
  }, [featureStore, searchQuery, page]);

  const reloadDataHandler = async () => await featureStore.findAsync(searchQuery, page);
  const searchHandler = () => history.push(paths.buildFeatureListPath(searchQueryCollector, `${1}`));
  const onPageHandler = (page: number) => history.push(paths.buildFeatureListPath(searchQueryCollector, `${page + 1}`));
  const createFeatureHandler = () => history.push(paths.buildFeatureCreatePath());
  const editFeatureHandler = async (feature: Feature) => history.push(paths.buildFeatureEditPath(feature.token || ''));
  const deleteFeatureHandler = async (feature: Feature) =>
  {
    if (window.confirm(uiStore.labels.pages.features.deleteFeatureConfirmation.apply({featureName: feature.name})))
    {
      await featureStore.deleteAsync(feature.token || '');
      if (!featureStore.isError)
      {
        await reloadDataHandler();
      }
    }
  };
  return (
    <article className="page feature-list">
      <h1>{uiStore.labels.pages.features.title}<SimpleInlineHelp>{uiStore.labels.help.features.about}</SimpleInlineHelp></h1>
      {
        featureStore.isLoading
        ? <Loading />
        : featureStore.isError
          ? <ErrorMessage>
              {featureStore.errorMessage}
              <button onClick={reloadDataHandler}>
                <LabeledIcon label={uiStore.labels.common.tryAgain}><ReloadIcon/></LabeledIcon>
              </button>
            </ErrorMessage>
          : <div className="content">
              <div className="action-bar">
                <button onClick={reloadDataHandler}><ReloadIcon/></button>
                <button onClick={createFeatureHandler}>{uiStore.labels.pages.features.createNewFeature}</button>
                <input type="text"
                       className="search"
                       placeholder={uiStore.labels.pages.features.searchForFeatures}
                       value={searchQueryCollector}
                       onChange={e => setSearchQueryCollector(prev => e.target.value)}
                       onKeyPress={async e => e.key === 'Enter' ? searchHandler() : null}/>
                <button onClick={searchHandler}><FindIcon/></button>
              </div>
              <div className="pagination">
                <Pagination currentPage={featureStore.pagedFeatures.page || 0}
                            totalPages={featureStore.pagedFeatures.total || 0}
                            onPage={onPageHandler}/>
              </div>
              {
                featureStore.pagedFeatures.data && featureStore.pagedFeatures.data.length > 0
                ? <FeatureList features={featureStore.pagedFeatures} deleteFeatureHandler={deleteFeatureHandler} editFeatureHandler={editFeatureHandler} />
                : <Message>{uiStore.labels.common.noResults}</Message>
              }
            </div>
      }
    </article>
  );
});